import React, { useMemo } from "react";
import { DateInput, Input } from "../../../../../../common/Input";
import { Select } from "../../../../../../common/Select";
import SectionBadge from "../../ui/SectionBadge";
import { SALARYTYPES } from "../../../../../../utils/enums";
import { FEATURES } from "../../../../../../config/features";
import SalaryRateCategorySelect from "../../../../../../common/selectComponents/SalaryRateCategorySelect";

const CompensationTab = ({
  form,
  set,
  options,
  editable,
  errors,
  matchedRules,
  loadingRates
}) => {
  const rulesExist = matchedRules.length > 0;
  const hasValidationError = !!errors.maintenanceSalaryCombinationV2Id;
  const showCriteriaWarning = !rulesExist && !loadingRates;

  const salaryRuleOptions = useMemo(() => {
    return matchedRules.map(rule => ({
      value: rule.id,
      label: `${rule.category} (Rate: ${rule.salary})`
    }));
  }, [matchedRules]);

  const handleRateCategoryChange = (e) => {
    const ruleId = e.target.value;
    const selectedRule = matchedRules.find(r => r.id.toString() === ruleId.toString());

    // Save the Header ID to the form
    set('maintenanceSalaryCombinationV2Id', ruleId);

    // Auto-update the rate amount if a rule is selected
    if (selectedRule) {
      set('rateAmount', selectedRule.salary);
    } else {
      set('rateAmount', "");
    }
  };

  return (
    <div className="space-y-6 pt-2">
      <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
        <SectionBadge label="Compensation Details" editable={editable} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4">
          <div className="space-y-4">
            <DateInput
              label="Date Hired:"
              value={form.dateHired || ""}
              disabled={true}
            />
            <Select
              label="Pay Type:"
              options={options.payTypes}
              value={form.payTypeId}
              onChange={e => set('payTypeId', e.target.value)}
              disabled={!editable}
              placeholder="--Select Pay Type--"
              isRequired={true}
              error={!!errors.payTypeId}
              errorMessage={errors.payTypeId}
            />
            <div>
              <p className="mb-1.5 block text-sm font-medium text-gray-600">Contract Period <span className="ml-1 text-red-500">*</span></p>
              <div className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                <DateInput
                  value={form.contractFrom || ""}
                  onChange={e => set('contractFrom', e.target.value)}
                  disabled={!editable}
                  isRequired={true}
                  error={!!errors.contractFrom}
                  errorMessage={errors.contractFrom}
                />
                <span className="text-sm text-gray-500 text-center">to</span>
                <DateInput
                  value={form.contractTo || ""}
                  onChange={e => set('contractTo', e.target.value)}
                  disabled={!editable}
                  isRequired={true}
                  error={!!errors.contractTo}
                  errorMessage={errors.contractTo}
                />
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <label className={`flex items-center gap-3 ${editable ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
                <input type="checkbox" checked={!!form.entitledToOvertime} onChange={e => set('entitledToOvertime', e.target.checked)} disabled={!editable} className="w-4 h-4 accent-blue-600" />
                <span className="text-sm font-medium text-gray-700">Entitled to Overtime</span>
              </label>
            </div>
          </div>
          <div className="space-y-4">
            <Select
              label="Salary Type:"
              options={options.salaryTypes}
              value={form.salaryType || ""}
              onChange={e => {
                set('salaryType', e.target.value);
              }}
              disabled={!editable}
              placeholder="--Select Salary Type--"
              isRequired={true}
              error={!!errors.salaryType}
              errorMessage={errors.salaryType}
            />
            {form.salaryType === SALARYTYPES.MAINTENANCESALARYCOMBINATION ? (
              <div className="space-y-4">
                <div className="relative">
                  <Select
                    label={`Salary Rate Category: ${loadingRates ? " (Searching...)" : ""}`}
                    options={salaryRuleOptions}
                    value={form.maintenanceSalaryCombinationV2Id}
                    onChange={handleRateCategoryChange}
                    isDisabled={!editable || loadingRates || !rulesExist}
                    placeholder={rulesExist ? "-- Select Matching Rate --" : "-- No rates found for criteria --"}

                    // Highlight red if there's a validation error OR if no rules match the criteria
                    error={hasValidationError || showCriteriaWarning}

                    // Only show the "Required" text if rules actually exist but weren't picked
                    errorMessage={hasValidationError ? errors.maintenanceSalaryCombinationV2Id : ""}
                    isRequired={true}
                  />

                  {/* 
                      Custom Criteria Warning: 
                      Only shows if the hook found 0 matches for the Dept/Pos/EmpType combo 
                  */}
                  {showCriteriaWarning && (
                    <div className="mt-1 flex items-start gap-2 p-2 bg-red-50 border border-red-100 rounded-md">
                      <span className="text-[10px] text-red-600 font-bold leading-tight">
                        No matching rules found.
                      </span>
                    </div>
                  )}
                </div>

                <Input
                  label="Daily Rate (Calculated):"
                  value={form.rateAmount || ""}
                  isDisabled={true}
                  placeholder="Select a category above"
                  // We can also show error here if the rate is missing
                  error={hasValidationError && !form.rateAmount}
                />
              </div>
            ) : (
              <Input
                label="Rate Amount:"
                type="number"
                value={form.rateAmount || ""}
                onChange={e => set('rateAmount', e.target.value)}
                disabled={!editable || !form.salaryType}
                placeholder="-- Enter Rate Amount --"
                isRequired={true}
                error={!!errors.rateAmount}
                errorMessage={errors.rateAmount}
              />

            )}
          </div>
        </div>
      </div>


    </div>
  );
};

export default CompensationTab;